import { add, format } from 'date-fns';
import { ChangeEvent, Dispatch, useEffect } from 'react';

import { Input } from '../../../../../components/input';
import { PaymentMultiuse } from '../../../../../models/payment-links-multiuse';
import { ExpirationTypesAllowed, TypesAllowed } from '../../../../../models/payment-links-multiuse';
import { ViewsAllowed } from '../../../../../models/payment-links-multiuse';
import { getNextHalfHour, transformDateToState } from '../../../../../utils/dates';
import { Values } from '../index';
import { ContainerInputsForm, HintInput, InputContainer, SectionCardsContainer } from '../styles';
import { PLCard } from './pl-card';
import { TypeExpiration } from './type-expiration';

interface CreatePaymentLinkProps {
	state: Values;
	setState: Dispatch<React.SetStateAction<Values>>;
	selectedToEdit?: PaymentMultiuse;
	view: ViewsAllowed;
}

const CreatePaymentLink = ({ state, setState, selectedToEdit, view }: CreatePaymentLinkProps) => {
	const handleVerifyDisabledButton = () => {
		//Agregar en caso de edicion
		if (!selectedToEdit) {
			if (state.amount !== '' && state.reference !== '') {
				return false;
			}
			return true;
		} else {
			//Si la referencia ingresada no es igual a la que ya tiene, se habilita el boton
			if (state.reference !== selectedToEdit.merchantorderid || state.description !== '') {
				return false;
			}
			return true;
		}
	};

	const setExpirationForUnique = () => {
		if (state.showConfigExpiration) {
			setState({
				...state,
				paymentType: 'unique',
				expirationType: 'date',
				initExpirationDate: transformDateToState(new Date()),
				endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
				initExpirationTime: format(new Date(), 'HH:mm'),
				endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
			});
		} else {
			setState({
				...state,
				paymentType: 'unique',
				expirationType: 'date',
				initExpirationDate: transformDateToState(new Date()),
				endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
				initExpirationTime: getNextHalfHour(format(new Date(), 'HH:mm')),
				endExpirationTime: getNextHalfHour(format(add(new Date(), { days: 1 }), 'HH:mm')),
			});
		}
	};

	const setExpirationForRecurrent = () => {
		if (state.expirationType === 'date') {
			if (state.showConfigExpiration) {
				setState({
					...state,
					paymentType: 'recurrent',
					expirationType: 'date',
					initExpirationDate: transformDateToState(new Date()),
					endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
					initExpirationTime: format(new Date(), 'HH:mm'),
					endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
				});
			} else {
				setState({
					...state,
					paymentType: 'recurrent',
					expirationType: 'date',
					initExpirationDate: transformDateToState(new Date()),
					endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
					initExpirationTime: getNextHalfHour(format(new Date(), 'HH:mm')),
					endExpirationTime: getNextHalfHour(format(add(new Date(), { days: 1 }), 'HH:mm')),
				});
			}
		} else {
			setState({
				...state,
				paymentType: 'recurrent',
				expirationType: 'num_payments',
				numPayments: 1,
				initExpirationDate: '',
				initExpirationTime: '',
				endExpirationDate: '',
				endExpirationTime: '',
			});
		}
	};

	const handleSelectPayType = (payType: TypesAllowed) => {
		if (state.paymentType !== payType) {
			if (payType === 'unique') {
				setExpirationForUnique();
			} else {
				setExpirationForRecurrent();
			}
		}
	};

	const handleExpirationTypeChange = (expirationType: ExpirationTypesAllowed) => {
		if (expirationType === 'date') {
			setState({
				...state,
				expirationType,
				initExpirationDate: transformDateToState(new Date()),
				endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
				initExpirationTime: getNextHalfHour(format(new Date(), 'HH:mm')),
				endExpirationTime: getNextHalfHour(format(add(new Date(), { days: 1 }), 'HH:mm')),
			});
		} else if (expirationType === 'num_payments') {
			setState({
				...state,
				expirationType,
				numPayments: 1,
				initExpirationDate: '',
				initExpirationTime: '',
				endExpirationDate: '',
				endExpirationTime: '',
			});
		}
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const regex = /^[0-9]*[.]?([0-9]{1,2})?$/;

		if ((name === 'numPayments' || name === 'amount') && !regex.test(value)) {
			return;
		}

		if ((name === 'numPayments' || name === 'amount') && value === '0') {
			return;
		}

		setState({ ...state, [name]: value });
	};

	const transformToNumber = (value: string) => {
		return Number(value.replace('$', '').replace(',', '').replace('.', ''));
	};

	useEffect(() => {
		const buttonDisabled = handleVerifyDisabledButton();
		setState({ ...state, buttonDisabled });
	}, [state.amount, state.reference, state.description]);

	useEffect(() => {
		if (state.paymentType === 'unique') {
			setState({ ...state, expirationType: 'date', selectButtonDisabled: true });
		} else {
			setState({ ...state, selectButtonDisabled: false });
		}
	}, [state.paymentType]);

	return (
		<ContainerInputsForm>
			<Input
				label="Monto"
				name="amount"
				variant="large"
				value={selectedToEdit ? transformToNumber(selectedToEdit.amount) : state.amount}
				prefixSymbol="$"
				onChange={handleChangeInput}
				placeholder="10.000"
				disabled={view === 'edit-payment-link'}
				labelFocus
				hasFocus
				fontSize="16px"
				type="number"
			/>

			{/* Referencia */}
			<InputContainer>
				<Input
					label="Referencia"
					name="reference"
					value={state.reference}
					onChange={handleChangeInput}
					variant="large"
					placeholder="Ej. nombre del cliente, referencia del cobro"
					labelFocus
					hasFocus
					fontSize="16px"
					type="text"
				/>
				<HintInput>Tus clientes verán este nombre durante el proceso de pago</HintInput>
			</InputContainer>

			<Input
				label="Descripción (opcional)"
				name="description"
				value={state.description || ''}
				onChange={handleChangeInput}
				variant="large"
				labelFocus
				hasFocus
				fontSize="16px"
				type="text"
			/>

			{/* Seleccionar tipo de cobro */}
			<SectionCardsContainer>
				<PLCard
					payType="unique"
					onClick={() => handleSelectPayType('unique')}
					selected={
						selectedToEdit
							? selectedToEdit?.paymenttype.toLowerCase() === 'unique'
							: state.paymentType === 'unique'
					}
					disabled={view === 'edit-payment-link'}
				></PLCard>
				<PLCard
					payType="recurrent"
					onClick={() => handleSelectPayType('recurrent')}
					selected={
						selectedToEdit
							? selectedToEdit?.paymenttype.toLowerCase() === 'recurrent'
							: state.paymentType === 'recurrent'
					}
					disabled={view === 'edit-payment-link'}
				/>
			</SectionCardsContainer>

			{/* Tipo de expiracion */}
			<TypeExpiration
				state={state}
				setState={setState}
				selectedToEdit={selectedToEdit}
				handleExpirationTypeChange={handleExpirationTypeChange}
				handleChangeInput={handleChangeInput}
			/>
		</ContainerInputsForm>
	);
};

export { CreatePaymentLink };
